import { call, delay, put } from 'redux-saga/effects';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { logoutRequest } from 'store/auth/actions';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import {
  invalidateReconciliationGenerateLoad,
  loadReconRequest,
} from 'store/vat/actions';
import {
  generateReconciliationPageSuccess,
  generateReconciliationComplete,
  generateReconciliationFailure,
  generateReconciliationInProgress,
  generateReconciliationRetryAfter,
} from '../actions';
import { generateReconciliation, GenerateReconciliationResponse } from '../api';
import { ReconciliationDataStatus } from '../models';
import { GenerateReconciliationRequestAction } from '../types';

const DEFAULT_IN_PROGRESS_DELAY_MS = 4000;
const DEFAULT_RETRY_AFTER_DELAY_MS = 5000;

function parseRetrySeconds(message: string): number {
  const match = message.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : DEFAULT_RETRY_AFTER_DELAY_MS / 1000;
}

export function* generateReconciliationSaga(
  action: GenerateReconciliationRequestAction
) {
  const { connectionId, mappingId, toDate, currency, pageSize, reportId } =
    action.payload;

  try {
    yield put(
      loadReconRequest(connectionId, toDate, mappingId, toDate, currency, {
        tiedToReconciliationGenerate: true,
      })
    );

    while (true) {
      const response: GenerateReconciliationResponse = yield call(
        generateReconciliation,
        connectionId,
        mappingId,
        toDate,
        1,
        1,
        reportId,
        currency,
        pageSize
      );
      const err: ApiError | null = checkResponse(response?.responseMessage);
      if (err) throw err;

      const data = response.data;

      if (
        !data.success &&
        data.dataStatus === ReconciliationDataStatus.IN_PROGRESS
      ) {
        yield put(generateReconciliationInProgress());
        yield delay(DEFAULT_IN_PROGRESS_DELAY_MS);
        continue;
      }

      if (
        !data.success &&
        data.dataStatus === ReconciliationDataStatus.RETRY_AFTER
      ) {
        const seconds = parseRetrySeconds(data.message);
        yield put(generateReconciliationRetryAfter(seconds));
        yield delay(seconds * 1000);
        continue;
      }

      yield put(generateReconciliationPageSuccess(data));
      yield put(generateReconciliationComplete());
      return;
    }
  } catch (error: any) {
    yield put(invalidateReconciliationGenerateLoad());
    // Same as generateReportSaga: attach ids so "New tax rates detected" links to returns.edit, not entities list
    const errorWithContext = {
      ...error,
      connectionId,
      mappingId,
    };
    const message: MessageData | null = renderErrorMessage(errorWithContext);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(generateReconciliationFailure());
  }
}
